import React, { useEffect, useContext, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import queryString from "query-string";
import styled from "styled-components/macro";
import ReactToPrint from "react-to-print";
import {
  getBaseURL,
  getCatalogId,
  getLangId,
  getStoreId,
  isBuyOnBehalf,
  getRunAsUserId,
} from "../../Shared/AppContext/selectors";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import {
  getCurrentFile,
  jsonApiHeader,
  checkHttpStatus,
} from "../../Shared/utility";
import PrintIcon from "../../assets/images/print-icon.svg";
import ShoppingCartEmptyCard from "./ShoppingCartEmptyCard";
import BreadCrumb from "../../Shared/Components/BreadCrumb";
import ShoppingCartCardItem from "./ShoppingCartCardItem";
import PrintQuote from "./PrintQuote";
import { ThreeDots, RotatingLines } from "react-loader-spinner";

const parseCartAmount = (value) => {
  const amount = Number.parseFloat(value);
  return Number.isFinite(amount) ? amount : null;
};

const calculateItemsSubtotal = (items = []) =>
  (Array.isArray(items) ? items : []).reduce((subtotal, item) => {
    const unitPrice =
      parseCartAmount(item?.unitPrice) ?? parseCartAmount(item?.listPrice) ?? 0;
    const quantity = parseCartAmount(item?.quantity) ?? 0;
    return subtotal + unitPrice * quantity;
  }, 0);

const getCartTotalCost = (cartData, fallbackItems = []) => {
  const cartTotal = parseCartAmount(cartData?.totalProductPrice);
  if (cartTotal !== null) {
    return cartTotal.toFixed(2);
  }
  return calculateItemsSubtotal(cartData?.orderItem || fallbackItems).toFixed(2);
};

const getDisplayTotalCost = (totalCost, fallbackItems = []) => {
  const parsedTotal = parseCartAmount(totalCost);
  return parsedTotal !== null ? parsedTotal : calculateItemsSubtotal(fallbackItems);
};

const ENRICHMENT_FETCH_TIMEOUT_MS = 12000;
const CART_LOADER_TIMEOUT_MS = 15000;

const fetchWithTimeout = (
  url,
  options,
  timeoutMs = ENRICHMENT_FETCH_TIMEOUT_MS
) => {
  if (typeof AbortController === "undefined") {
    let timeoutId;
    return Promise.race([
      fetch(url, options),
      new Promise((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error(`Timed out loading ${url}`)),
          timeoutMs
        );
      }),
    ]).finally(() => clearTimeout(timeoutId));
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, {
    ...(options || {}),
    signal: controller.signal,
  }).finally(() => clearTimeout(timeoutId));
};

const getUrlOrderId = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const urlOrderId = new URLSearchParams(window.location.search).get("orderId");
  return urlOrderId && /^\d+$/.test(urlOrderId) ? urlOrderId : null;
};

const ShoppingCart = (props) => {
  const storeId = useSelector(getStoreId);
  const catalogId = useSelector(getCatalogId);
  const langId = useSelector(getLangId);
  const baseURL = useSelector(getBaseURL);
  const isBOB = useSelector(isBuyOnBehalf);
  const runAsUserId = useSelector(getRunAsUserId);

  const createLinks = {
    orderFavorites: `${baseURL}PersonalizedOrderFormListView?langId=${langId}&storeId=${storeId}&catalogId=${catalogId}`,
    catalog: `${baseURL}StoreCatalogDisplay?langId=${langId}&storeId=${storeId}&catalogId=${catalogId}&showcat=Y`,
    myOrders: `${baseURL}OrderInfoSearchView?function=next&pageNbr=0&lastOrderNbr=0&lastOrgnNbr=0&storeId=${storeId}&langId=${langId}&catalogId=${catalogId}`,
  };

  const [shoppingCartItems, setShoppingCartItems] = useState([]);
  const [orderId, setOrderId] = useState(null);
  const [totalCost, setTotalCost] = useState(null);
  const [loadingCost, setLoadingCost] = useState(false);
  const [shipModeId, setShipModeId] = useState(null);
  const [dicountCodes, setDicountCodes] = useState([]);
  const [appliedDiscountCode, setAppliedDiscountCode] = useState(null);
  const [isCartLocked, setIsCartLocked] = useState(false);
  const [isMainLoading, setIsMainLoading] = useState(true);
  const [isApplyDiscountButtonEnabled, setIsApplyDiscountButtonEnabled] =
    useState(false);
  const [selectedOrderItemIds, setSelectedOrderItemIds] = useState([]);
  const [groupedLineItems, setGroupedLineItems] = useState([]);
  const [isLockActionLoading, setIsLockActionLoading] = useState(false);
  const printQuoteRef = useRef(null);
  const pendingOrderInitializedRef = useRef(null);

  const {
    selectedLanguage = "",
    getCartItemsCount,
    cartItemsQty,
    cartItemsData,
    orderId: orderid,
  } = useContext(UserContext);

  // Add currency formatter here
  const currency =
  cartItemsData?.grandTotalCurrency?.trim()?.toUpperCase() ||
  cartItemsData?.totalProductPriceCurrency?.trim()?.toUpperCase() ||
  "USD";

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency,
});
  
  var currentFileRequired = getCurrentFile(selectedLanguage);
  const { t: translationKey } = useTranslation();

  const groupedCartItems = useMemo(() => {
    if (!groupedLineItems?.length || !shoppingCartItems?.length) {
      return null;
    }
    const itemMap = new Map(
      shoppingCartItems.map((item) => [
        String(item.orderItemId),
        item,
      ])
    );
    return groupedLineItems
      .map((group) => {
        const lineItems = (group.lineItems || [])
          .map((lineItem) => itemMap.get(String(lineItem.orderItemId)) || null)
          .filter(Boolean);
        return {
          ...group,
          lineItems,
        };
      })
      .filter((group) => group.lineItems.length);
  }, [groupedLineItems, shoppingCartItems]);

  const selectableOrderItemIds = useMemo(
    () =>
      shoppingCartItems
        .filter((item) => item?.orderItemId)
        .map((item) => String(item.orderItemId)),
    [shoppingCartItems]
  );

  const hasSelection = selectedOrderItemIds.length > 0;
  const allItemsSelected =
    selectableOrderItemIds.length > 0 &&
    selectedOrderItemIds.length === selectableOrderItemIds.length;

  const hasDiscountInSelection = useMemo(() => {
    if (!selectedOrderItemIds.length) {
      return false;
    }
    const selectedIds = new Set(selectedOrderItemIds);
    return shoppingCartItems.some((item) => {
      if (!item?.orderItemId || !selectedIds.has(String(item.orderItemId))) {
        return false;
      }
      const discountValue = parseFloat(item?.percentageDiscount);
      return (
        (item?.promoCode && item.promoCode.trim().length > 0) ||
        (item?.promocode && item.promocode.trim().length > 0) ||
        (Number.isFinite(discountValue) && discountValue !== 0)
      );
    });
  }, [shoppingCartItems, selectedOrderItemIds]);

  const refreshCartData = async () => {
    const latestCartData = await getCartItemsCount(storeId);
    const latestOrderItems = Array.isArray(latestCartData?.orderItem)
      ? latestCartData.orderItem
      : [];

    setIsCartLocked(latestCartData?.locked === "true");
    setOrderId(latestCartData?.orderId || null);
    setTotalCost(getCartTotalCost(latestCartData, latestOrderItems));

    if (!latestOrderItems.length) {
      setShoppingCartItems([]);
      setGroupedLineItems([]);
      setSelectedOrderItemIds([]);
      setShipModeId(null);
      setIsMainLoading(false);
      return latestCartData;
    }

    setShipModeId(latestOrderItems[0]?.shipModeId);
    try {
      const groupedResponse = await fetch(
        `/wcs/resources/store/${storeId}/3mcart/lineitems/order/${latestCartData.orderId}`,
        {
          method: "GET",
          headers: jsonApiHeader(),
        }
      );
      const groupedData = await checkHttpStatus(groupedResponse);
      setGroupedLineItems(
        (groupedData && groupedData.groupedLineItemsList) || []
      );
    } catch (error) {
      console.log("error", error);
      setGroupedLineItems([]);
    }

    getProductItemDetails(
      latestOrderItems,
      null,
      null,
      latestCartData.orderId,
      dicountCodes
    );
    return latestCartData;
  };

  const fetchDiscountCodes = async () => {
    try {
      await fetch(`/wcs/resources/store/${storeId}/shopcart/getDiscountCodes`)
        .then(checkHttpStatus)
        .then((response) => setDicountCodes(response.resultList));
    } catch (error) {
      console.log("error", error);
    }
  };

  const fetchCartStatus = async () => {
    try {
      await fetch(`/wcs/resources/store/${storeId}/cart/@self`)
        .then(checkHttpStatus)
        .then((data) => setIsCartLocked(data.locked === "true"));
    } catch (error) {
      console.log("error", error);
    }
  };

  const postToFirstAvailableEndpoint = async (endpointUrls, body = {}) => {
    const token = document.getElementById("reactWCToken")?.value;
    let lastError = null;
    for (const endpointUrl of endpointUrls) {
      try {
        const response = await fetch(endpointUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            WCAuthToken: token
              ? token
              : "331732%2CuCQCTk7XVbnjdyVybYHGBu6BZYfl2T6u3NwTI0o4oN0%3D",
          },
          body: JSON.stringify(body),
        });
        if (response.ok) {
          return checkHttpStatus(response);
        }
        lastError = new Error(
          `Cart lock endpoint failed (${response.status}) at ${endpointUrl}`
        );
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError || new Error("Cart lock endpoint unavailable");
  };

  const setUrlOrderAsPendingOrder = async (urlOrderId) => {
    if (
      !urlOrderId ||
      pendingOrderInitializedRef.current === urlOrderId
    ) {
      return;
    }

    pendingOrderInitializedRef.current = urlOrderId;
    const token = document.getElementById("reactWCToken")?.value;
    try {
      const response = await fetchWithTimeout(
        `/wcs/resources/store/${storeId}/cart/${urlOrderId}/set_pending_order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            WCAuthToken: token ? token : "",
          },
          body: JSON.stringify({}),
        }
      );
      await checkHttpStatus(response);
    } catch (error) {
      console.log("error setting URL order as pending", error);
    }
  };

  const loadCartData = async (cartPath) => {
    const response = await fetchWithTimeout(
      `/wcs/resources/store/${storeId}/cart/${cartPath}`,
      {
        method: "GET",
        headers: jsonApiHeader(),
      }
    );
    return checkHttpStatus(response);
  };

  const loadCartForPage = async (urlOrderId) => {
    const selfCartData = await loadCartData("@self");
    if (
      !urlOrderId ||
      !selfCartData?.orderId ||
      String(selfCartData.orderId) === urlOrderId
    ) {
      return selfCartData;
    }

    if (urlOrderId && selfCartData?.orderId) {
      try {
        return await loadCartData(urlOrderId);
      } catch (error) {
        console.log("error loading URL cart order", error);
      }
    }

    return selfCartData;
  };

  const UnLockCart = async () => {
    const currentOrderId = cartItemsData?.orderId;
    if (!currentOrderId || isLockActionLoading) {
      return;
    }
    setIsLockActionLoading(true);
    try {
      const endpointUrls = isBOB
        ? [
            `/wcs/resources/store/${storeId}/cart/${currentOrderId}/unlockOnBehalf`,
          ]
        : [
            `/wcs/resources/store/${storeId}/cart/${currentOrderId}/unlock`,
            `/wcs/resources/store/${storeId}/cart/${currentOrderId}/unlockOnBehalf`,
          ];
      await postToFirstAvailableEndpoint(endpointUrls);
      await fetchCartStatus();
    } catch (error) {
      console.log("error unlocking cart", error);
    } finally {
      setIsLockActionLoading(false);
    }
  };

  const LockCart = async () => {
    const currentOrderId = cartItemsData?.orderId;
    if (!currentOrderId || isLockActionLoading) {
      return;
    }
    setIsLockActionLoading(true);
    try {
      const endpointUrls = isBOB
        ? [`/wcs/resources/store/${storeId}/cart/${currentOrderId}/lockOnBehalf`]
        : [
            `/wcs/resources/store/${storeId}/cart/${currentOrderId}/lock`,
            `/wcs/resources/store/${storeId}/cart/${currentOrderId}/lockOnBehalf`,
          ];
      await postToFirstAvailableEndpoint(endpointUrls);
      await fetchCartStatus();
    } catch (error) {
      console.log("error locking cart", error);
    } finally {
      setIsLockActionLoading(false);
    }
  };

  const handleSwitchToggle = async () => {
    if (isLockActionLoading) {
      return;
    }
    if (isCartLocked === true) {
      await UnLockCart();
    } else {
      await LockCart();
    }
  };

  useEffect(() => {
    if (!isMainLoading) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      setLoadingCost(false);
      setIsMainLoading(false);
    }, CART_LOADER_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [isMainLoading]);

  useEffect(() => {
    if (storeId) {
      async function cartPageRequest() {
        let getCartData = null;
        const urlOrderId = getUrlOrderId();
        try {
          await setUrlOrderAsPendingOrder(urlOrderId);
          getCartData = await loadCartForPage(urlOrderId);
          const orderItem = Array.isArray(getCartData?.orderItem)
            ? getCartData.orderItem
            : [];
          setIsCartLocked(getCartData.locked === "true");
          const activeOrderId =
            getCartData?.orderId || urlOrderId || cartItemsData?.orderId;
          setOrderId(activeOrderId);
          const activeCartData = getCartData || cartItemsData;
          if (activeCartData?.orderItem && activeCartData.orderItem.length) {
            setShipModeId(activeCartData?.orderItem[0]?.shipModeId);
            setTotalCost(getCartTotalCost(activeCartData));
          }
          if (!orderItem.length) {
            setShoppingCartItems([]);
            setGroupedLineItems([]);
            setSelectedOrderItemIds([]);
            setShipModeId(null);
            setTotalCost(getCartTotalCost(getCartData, orderItem));
            setIsMainLoading(false);
            return;
          }
          setShoppingCartItems(orderItem);
          setLoadingCost(false);
          setIsMainLoading(false);

          let discountCodeResultList = [];
          try {
            const getDiscountCodesResponse = await fetchWithTimeout(
              `/wcs/resources/store/${storeId}/shopcart/getDiscountCodes`
            );
            const discountCodesList = await checkHttpStatus(
              getDiscountCodesResponse
            );
            if (discountCodesList) {
              discountCodeResultList = discountCodesList.resultList || [];
              setDicountCodes(discountCodeResultList);
            }
          } catch (error) {
            console.log("error loading discount codes", error);
          }

          if (getCartData) {
            setTotalCost(getCartTotalCost(getCartData, orderItem));
            try {
              const groupedResponse = await fetchWithTimeout(
                `/wcs/resources/store/${storeId}/3mcart/lineitems/order/${activeOrderId}`,
                {
                  method: "GET",
                  headers: jsonApiHeader(),
                }
              );
              const groupedData = await checkHttpStatus(groupedResponse);
              setGroupedLineItems(
                (groupedData && groupedData.groupedLineItemsList) || []
              );
            } catch (error) {
              console.log("error", error);
            }
            await getProductItemDetails(
              shoppingCartItems.length ? shoppingCartItems : orderItem,
              null,
              null,
              activeOrderId,
              discountCodeResultList
            );
          }
        } catch (error) {
          console.log("error loading cart", error);
          const fallbackCartData = getCartData || cartItemsData;
          const fallbackOrderItems = Array.isArray(fallbackCartData?.orderItem)
            ? fallbackCartData.orderItem
            : [];
          const fallbackOrderId =
            fallbackCartData?.orderId || urlOrderId || orderId;
          setShoppingCartItems(fallbackOrderItems);
          setOrderId(fallbackOrderId);
          if (fallbackCartData?.totalProductPrice) {
            setTotalCost(getCartTotalCost(fallbackCartData, fallbackOrderItems));
          }
          setLoadingCost(false);
          setIsMainLoading(false);
          if (fallbackOrderItems.length) {
            await getProductItemDetails(
              fallbackOrderItems,
              null,
              null,
              fallbackOrderId,
              dicountCodes || []
            );
          }
        }
      }
      cartPageRequest();
    }

    // fetchDiscountCodes();

    //fetchCartStatus();

    //fetchLatestCartItem();
  }, [storeId, cartItemsData]);

  useEffect(() => {
    const contextOrderItems = Array.isArray(cartItemsData?.orderItem)
      ? cartItemsData.orderItem
      : [];
    if (!contextOrderItems.length || shoppingCartItems.length) {
      return;
    }

    setShoppingCartItems(contextOrderItems);
    setOrderId(cartItemsData?.orderId);
    if (cartItemsData?.totalProductPrice) {
      setTotalCost(getCartTotalCost(cartItemsData, contextOrderItems));
    }
    if (contextOrderItems[0]?.shipModeId) {
      setShipModeId(contextOrderItems[0].shipModeId);
    }
    setLoadingCost(false);
    setIsMainLoading(false);
    getProductItemDetails(
      contextOrderItems,
      null,
      null,
      cartItemsData?.orderId,
      dicountCodes || []
    );
  }, [cartItemsData, shoppingCartItems.length]);

  useEffect(() => {
    const selectableIds = new Set(selectableOrderItemIds);
    setSelectedOrderItemIds((currentSelection) =>
      currentSelection.filter((id) => selectableIds.has(id))
    );
  }, [selectableOrderItemIds]);

  useEffect(() => {
    setIsApplyDiscountButtonEnabled(
      Boolean(appliedDiscountCode) && hasSelection
    );
  }, [appliedDiscountCode, hasSelection]);

  const getProductItemDetails = async (
    orderItems,
    orderItemIds,
    updatedItemsWithDiscountedData,
    currentCartOrderId,
    discountCodesList,
    newCartData // This is passed because we need to update unit price of each item
  ) => {
    let orderItemsUpdatedData = Array.isArray(orderItems)
      ? orderItems.map((item) => ({ ...item }))
      : orderItems;

    // This condition runs when we apply promo code and cartItems local data is already present
    if (orderItemIds && orderItemIds.length) {
      if (isBOB) {
        let orderItemIDS = [];

        if (
          updatedItemsWithDiscountedData &&
          updatedItemsWithDiscountedData.promoDetails &&
          updatedItemsWithDiscountedData.promoDetails.length
        ) {
          updatedItemsWithDiscountedData.promoDetails.forEach((item) => {
            orderItemIDS.push(item.orderItemId);
          });
          orderItemIDS.forEach((itemData) => {
            let itemIdx = orderItemsUpdatedData.findIndex(
              (data) => data.orderItemId === itemData
            );

            let updatedObjFromApiResponse =
              updatedItemsWithDiscountedData.promoDetails.find(
                (item) => item.orderItemId === itemData
              );

            let newCartSingleItemResponse = newCartData.find(
              (data) => data.orderItemId === itemData
            );

            if (updatedObjFromApiResponse) {
              let currentDiscoundCodeObj = discountCodesList.find(
                (discoundObj) =>
                  discoundObj.promoCode === updatedObjFromApiResponse.promoCode
              );
              const promoCode = updatedObjFromApiResponse.promoCode;
              const promoDescription =
                currentDiscoundCodeObj?.promoDescription || promoCode || "";

              orderItemsUpdatedData[itemIdx] = {
                ...orderItemsUpdatedData[itemIdx],
                listPrice: updatedObjFromApiResponse.listPrice,
                promoError: updatedObjFromApiResponse.promoError,
                percentageDiscount:
                  updatedObjFromApiResponse.percentageDiscount,
                promoCode,
                promoDescription,
                promocode: promoDescription,
                unitPrice: newCartSingleItemResponse?.unitPrice,
              };
            }
          });
        }
        setSelectedOrderItemIds([]);
      }
      setShoppingCartItems(orderItemsUpdatedData);
      setLoadingCost(false);
      setIsMainLoading(false);
      return orderItemsUpdatedData;
    }

    if (!Array.isArray(orderItems) || !orderItems.length) {
      setShoppingCartItems([]);
      setLoadingCost(false);
      setIsMainLoading(false);
      return [];
    }

    await Promise.all(
      orderItems.map(async (data, dataIndex) => {
        const query = queryString.stringify({
          catalogId,
          profileName: "3M_findProductByIds_Summary",
        });

        if (data.partNumber === "BANDORDER") {
          try {
            const bandOrderResposne = await fetchWithTimeout(
              `/wcs/resources/store/${storeId}/bandorder/byOrderItem/${data.orderItemId}`,
              {
                method: "GET",
                headers: jsonApiHeader(),
              }
            );
            const bandResponse = await checkHttpStatus(bandOrderResposne);
            if (bandResponse) {
              const data = bandResponse?.resultList?.[0];
              const hasBandOrderItems = Array.isArray(data?.items);
              if (!data || !hasBandOrderItems) {
                console.warn(
                  `Missing band order detail for orderItemId=${data?.orderItemId || orderItemsUpdatedData[dataIndex]?.orderItemId}`
                );
              } else {
                const bandOrderData = {
                  header: "Band Order",
                  items: data.items,
                  ...data,
                };
                orderItemsUpdatedData[dataIndex] = {
                  ...orderItemsUpdatedData[dataIndex],
                  bandOrderData,
                };
              }
            }
          } catch (error) {
            console.warn(
              `Failed to load band order detail for orderItemId=${orderItemsUpdatedData[dataIndex]?.orderItemId}`,
              error
            );
          }
        }
        //fetching each product detail
        let response = null;
        try {
          const productDetails = await fetchWithTimeout(
            `/search/resources/store/${storeId}/productview/byId/${data.productId}?${query}`,
            {
              method: "GET",
              headers: jsonApiHeader(),
            }
          );
          response = await checkHttpStatus(productDetails);
        } catch (error) {
          console.log("error loading product details", error);
        }

        if (response) {
          let prescriptionsAvailable = "";
          if (
            response.catalogEntryView &&
            response.catalogEntryView.length &&
            response.catalogEntryView[0].attributes &&
            response.catalogEntryView[0].attributes.length
          ) {
            let entryWithPrescription =
              response.catalogEntryView[0].attributes.find(
                (data) => data.identifier === "Prescription"
              );
            if (
              entryWithPrescription &&
              entryWithPrescription.values &&
              entryWithPrescription.values.length
            ) {
              entryWithPrescription.values.forEach((attributeData) => {
                prescriptionsAvailable =
                  prescriptionsAvailable !== ""
                    ? prescriptionsAvailable + "," + attributeData.identifier
                    : prescriptionsAvailable + attributeData.identifier;
              });
            }
          }

          const parentCatalogEntryID =
            response.catalogEntryView &&
            response.catalogEntryView.length &&
            response.catalogEntryView[0].parentCatalogEntryID;

          const parentCatalogGroupID =
            response.catalogEntryView &&
            response.catalogEntryView.length &&
            response.catalogEntryView[0].parentCatalogGroupID;

          orderItemsUpdatedData[dataIndex] = {
            ...orderItemsUpdatedData[dataIndex],
            shortDescription:
              response.catalogEntryView &&
              response.catalogEntryView.length &&
              response.catalogEntryView[0].shortDescription,
            prescriptionsAvailable,
            parentCatalogEntryID,
            parentCatalogGroupID,
          };
          const query = queryString.stringify({
            catalogId,
            profileName: "3M_findProductByIds_Summary",
          });

          //recalling the API to get the thumbnail from parentCatalogEntryId
          try {
            const thumbnailData = await fetchWithTimeout(
              `/search/resources/store/${storeId}/productview/byId/${parentCatalogEntryID}?${query}`,
              {
                method: "GET",
                headers: jsonApiHeader(),
              }
            );
            const thumbnaiResponse = await checkHttpStatus(thumbnailData);
            if (thumbnaiResponse) {
              orderItemsUpdatedData[dataIndex] = {
                ...orderItemsUpdatedData[dataIndex],
                thumbnail:
                  thumbnaiResponse.catalogEntryView &&
                  thumbnaiResponse.catalogEntryView.length &&
                  thumbnaiResponse.catalogEntryView[0].thumbnail,
              };
            }
          } catch (error) {
            console.log("error loading product thumbnail", error);
          }
        }
      })
    );

    orderItemsUpdatedData.sort(function (a, b) {
      // Turn your strings into dates, and then subtract them
      // to get a value that is either negative, positive, or zero.
      return new Date(b.createDate) - new Date(a.createDate);
    });

    //setting the promo fields from initial load
    if (isBOB) {
      const token = document.getElementById("reactWCToken")?.value;
      try {
        const updatedItemsWithDiscountedDataResponse = await fetchWithTimeout(
          `/wcs/resources/store/${storeId}/shopcart/getDiscountDetails?cartId=${currentCartOrderId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              WCAuthToken: token
                ? token
                : "331732%2CuCQCTk7XVbnjdyVybYHGBu6BZYfl2T6u3NwTI0o4oN0%3DD",
            },
          }
        );

        const updatedItemsWithDiscountedData = await checkHttpStatus(
          updatedItemsWithDiscountedDataResponse
        );
        let orderItemIDS = [];

        if (
          updatedItemsWithDiscountedData &&
          updatedItemsWithDiscountedData.promoDetails &&
          updatedItemsWithDiscountedData.promoDetails.length
        ) {
          updatedItemsWithDiscountedData.promoDetails.forEach((item) => {
            orderItemIDS.push(item.orderItemId);
          });
          orderItemIDS.forEach((itemData) => {
            let itemIdx = orderItemsUpdatedData.findIndex(
              (data) => data.orderItemId === itemData
            );
            let updatedObjFromApiResponse =
              updatedItemsWithDiscountedData.promoDetails.find(
                (item) => item.orderItemId === itemData
              );
            if (updatedObjFromApiResponse && itemIdx >= 0) {
              let currentDiscoundCodeObj = discountCodesList.find(
                (discoundObj) =>
                  discoundObj.promoCode === updatedObjFromApiResponse.promoCode
              );
              const promoCode = updatedObjFromApiResponse.promoCode;
              const promoDescription =
                currentDiscoundCodeObj?.promoDescription || promoCode || "";

              orderItemsUpdatedData[itemIdx] = {
                ...orderItemsUpdatedData[itemIdx],
                listPrice: updatedObjFromApiResponse.listPrice,
                promoError: updatedObjFromApiResponse.promoError,
                percentageDiscount:
                  updatedObjFromApiResponse.percentageDiscount,
                promoCode,
                promoDescription,
                promocode: promoDescription,
              };
            }
          });
        }
      } catch (error) {
        console.log("error loading discount details", error);
      }
      setSelectedOrderItemIds([]);
    }

    setShoppingCartItems(orderItemsUpdatedData);
    setLoadingCost(false);
    setIsMainLoading(false);
    return orderItemsUpdatedData;
  };

  const proceedToCheckoutPage = () => {
    window.location.href = `${baseURL}OrderProfileUpdate?URL=QuickCheckoutView&X_URL=StoreAppView&langId=${langId}&storeId=${storeId}&catalogId=${catalogId}&orderId=${orderId}&shipModeId=${shipModeId}#/checkout`;
  };
  
  let USDollar = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  const contextOrderItems = Array.isArray(cartItemsData?.orderItem)
    ? cartItemsData.orderItem
    : [];
  const activeShoppingCartItems = shoppingCartItems.length
    ? shoppingCartItems
    : contextOrderItems;
  const activeOrderId = orderId || cartItemsData?.orderId || orderid;
  const numericCartItemsQty = Number(cartItemsQty) || 0;
  const hasShoppingCartItems = activeShoppingCartItems.length > 0;
  const displayCartQty = numericCartItemsQty || activeShoppingCartItems.length;
  const isUrlOrderPage = Boolean(getUrlOrderId());
  const showMainLoader =
    isMainLoading &&
    !isUrlOrderPage &&
    numericCartItemsQty > 0 &&
    !hasShoppingCartItems;
  const showEmptyCart =
    !isMainLoading &&
    !loadingCost &&
    (displayCartQty === 0 ||
      (displayCartQty > 0 && !hasShoppingCartItems));

  //331732%2Cj8XUiWarAj3AqkOPkw8i6pYVCcfqi1S0FjPnUNnQ1Iw%3D

  //   {
  //     "storeId":"10051",
  //     "langId":"-1",
  //     "catalogId":"16557",
  //     "orderItemId_2":"2883205",
  //     "orderItemId_1":"2883204",
  //     "itemPriceCode_2":"20108",
  //     "itemPriceCode_1":"",
  //     "calculateOrder":"1",
  //     "forUserId":"31284"

  // }

  const onItemSelectionChange = (orderItemId, isChecked) => {
    if (!orderItemId) {
      return;
    }
    const itemId = String(orderItemId);
    setSelectedOrderItemIds((currentSelection) => {
      if (isChecked) {
        if (currentSelection.includes(itemId)) {
          return currentSelection;
        }
        return [...currentSelection, itemId];
      }
      return currentSelection.filter((id) => id !== itemId);
    });
  };

  const selectAllItems = () => {
    setSelectedOrderItemIds(selectableOrderItemIds);
  };

  const deselectAllItems = () => {
    setSelectedOrderItemIds([]);
  };

  const applyDiscountHandler = async () => {
    if (selectedOrderItemIds.length) {
      const runAsUserId = document.getElementById("runAsUserId")?.value;
      let applyDiscountCurrentData = {
        langId,
        storeId,
        catalogId,
        runAsUserId,
        calculateOrder: "1",
      };

      let count = 1;
      const targetItems = shoppingCartItems.filter(
        (item) =>
          item?.orderItemId &&
          selectedOrderItemIds.includes(String(item.orderItemId))
      );

      targetItems.forEach((item) => {
        if (item?.orderItemId) {
          applyDiscountCurrentData = {
            ...applyDiscountCurrentData,
            [`orderItemId_${count}`]: item.orderItemId,
            [`itemPriceCode_${count}`]: appliedDiscountCode,
          };
          count++;
        }
      });

      if (applyDiscountCurrentData.orderItemId_1) {
        const token = document.getElementById("reactWCToken")?.value;

        const applyPromoResponse = await fetch(
          `/wcs/resources/store/${storeId}/shopcart/applyDiscountCode`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              WCAuthToken: token
                ? token
                : "331732%2CuCQCTk7XVbnjdyVybYHGBu6BZYfl2T6u3NwTI0o4oN0%3D",
            },
            body: JSON.stringify(applyDiscountCurrentData),
          }
        );

        const applyPromoData = await checkHttpStatus(applyPromoResponse);

        if (applyPromoData.orderId && applyPromoData.orderId.length) {
          const orderItemIds = applyPromoData.orderItemId; //This Id's are stored because we will update our main shopping cart items data for these items

          const updatedItemsWithDiscountedDataResponse = await fetch(
            `/wcs/resources/store/${storeId}/shopcart/getDiscountDetails?cartId=${applyPromoData.orderId[0]}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                WCAuthToken: token
                  ? token
                  : "331732%2CuCQCTk7XVbnjdyVybYHGBu6BZYfl2T6u3NwTI0o4oN0%3D",
              },
            }
          );

          const updatedItemsWithDiscountedData = await checkHttpStatus(
            updatedItemsWithDiscountedDataResponse
          );

          if (
            updatedItemsWithDiscountedData &&
            updatedItemsWithDiscountedData.promoDetails &&
            updatedItemsWithDiscountedData.promoDetails.length
          ) {
            setLoadingCost(true);
            setIsApplyDiscountButtonEnabled(false);
            //caliing self cart API again to get the updated unit price

            const response = await fetch(
              `/wcs/resources/store/${storeId}/cart/@self`,
              {
                method: "GET",
                headers: jsonApiHeader(),
              }
            );
            const responsegetCartItemsData = await checkHttpStatus(response);
            setTotalCost(getCartTotalCost(responsegetCartItemsData));
            setAppliedDiscountCode(null);
            setSelectedOrderItemIds([]);
            if (document.getElementById("discount-dropdown")) {
              document.getElementById("discount-dropdown").value = "";
            }

            await getProductItemDetails(
              shoppingCartItems,
              orderItemIds,
              updatedItemsWithDiscountedData,
              applyPromoData.orderId[0],
              dicountCodes,
              responsegetCartItemsData.orderItem
            );
          }
        }
      }
    }
  };

  const removeDiscountHandler = async () => {
    if (!shoppingCartItems?.length) {
      return;
    }

    const orderItemIds =
      shoppingCartItems
        .filter(
          (item) =>
            item?.orderItemId &&
            selectedOrderItemIds.includes(String(item.orderItemId))
        )
        .filter((item) => {
          const discountValue = parseFloat(item?.percentageDiscount);
          return (
            (item?.promoCode && item.promoCode.trim().length > 0) ||
            (item?.promocode && item.promocode.trim().length > 0) ||
            (Number.isFinite(discountValue) && discountValue !== 0)
          );
        })
        .map((item) => item.orderItemId);

    if (!orderItemIds.length) {
      return;
    }

    const runAsUserId = document.getElementById("runAsUserId")?.value;
    const token = document.getElementById("reactWCToken")?.value;

    try {
      setLoadingCost(true);

      for (const orderItemId of orderItemIds) {
        const removeDiscountObj = {
          langId,
          storeId,
          catalogId,
          runAsUserId,
          calculateOrder: "1",
          orderItemId_1: orderItemId,
          itemPriceCode_1: "",
        };

        const removePromoResponse = await fetch(
          `/wcs/resources/store/${storeId}/shopcart/removeDiscountCode`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              WCAuthToken: token
                ? token
                : "331732%2CuCQCTk7XVbnjdyVybYHGBu6BZYfl2T6u3NwTI0o4oN0%3D",
            },
            body: JSON.stringify(removeDiscountObj),
          }
        );

        await checkHttpStatus(removePromoResponse);
      }

      const getCartItemsResponse = await fetch(
        `/wcs/resources/store/${storeId}/cart/@self`,
        {
          method: "GET",
          headers: jsonApiHeader(),
        }
      );
      const getCartData = await checkHttpStatus(getCartItemsResponse);
      if (getCartData?.orderItem?.length) {
        let updatedShoppingCartItems = [...shoppingCartItems];
        orderItemIds.forEach((orderItemId) => {
          const currentIndexToBeUpdated = updatedShoppingCartItems.findIndex(
            (data) => data.orderItemId === orderItemId
          );
          const updatedItemObj = getCartData.orderItem.find(
            (data) => data.orderItemId === orderItemId
          );
          if (currentIndexToBeUpdated !== -1) {
            updatedShoppingCartItems[currentIndexToBeUpdated] = {
              ...updatedShoppingCartItems[currentIndexToBeUpdated],
              promocode: null,
              promoCode: null,
              promoDescription: null,
              promoError: null,
              percentageDiscount: null,
              unitPrice:
                updatedItemObj?.unitPrice ||
                updatedShoppingCartItems[currentIndexToBeUpdated].unitPrice,
            };
          }
        });
        setShoppingCartItems(updatedShoppingCartItems);
        setTotalCost(getCartTotalCost(getCartData, updatedShoppingCartItems));
      }

      setIsApplyDiscountButtonEnabled(false);
      setAppliedDiscountCode(null);
      setSelectedOrderItemIds([]);
      if (document.getElementById("discount-dropdown")) {
        document.getElementById("discount-dropdown").value = "";
      }
    } catch (error) {
      console.log("error removing discount", error);
    } finally {
      setLoadingCost(false);
    }
  };

  const deleteAllItems = async (e) => {
    e.preventDefault();
    let OrderIdsToBeDeleted = [...selectedOrderItemIds];

    if (OrderIdsToBeDeleted.length) {
      setLoadingCost(true);
      const payloadData = OrderIdsToBeDeleted.map((orderItemId) => {
        return {
          orderItemId,
          quantity: "0",
        };
      });

      const deleteAllItemsPayload = {
        orderItem: payloadData,
      };

      const token = document.getElementById("reactWCToken")?.value;

      try {
        const deleteAllItemsData = await fetch(
          `/wcs/resources/store/${storeId}/cart/@self`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              WCAuthToken: token
                ? token
                : "331732%2CuCQCTk7XVbnjdyVybYHGBu6BZYfl2T6u3NwTI0o4oN0%3D",
            },
            body: JSON.stringify(deleteAllItemsPayload),
          }
        );

        await checkHttpStatus(deleteAllItemsData);
        await refreshCartData();
      } catch (error) {
        console.log("error deleting selected cart items", error);
      } finally {
        setSelectedOrderItemIds([]);
        setAppliedDiscountCode(null);
        setIsApplyDiscountButtonEnabled(false);
        if (document.getElementById("discount-dropdown")) {
          document.getElementById("discount-dropdown").value = "";
        }
        setLoadingCost(false);
      }
    }
  };

  return (
    <>
      {showMainLoader ? (
        <div className="loader-wrapper">
          <div className="loader-inner">
            <RotatingLines
              strokeColor="#5A5A5A"
              strokeWidth="5"
              animationDuration="0.75"
              width="45"
              visible={true}
            />
            <p className="loader-text">
              {translationKey(currentFileRequired["LOADING_YOUR_CART"])}
            </p>
          </div>
        </div>
      ) : (
        <>
          <BreadCrumb
            currentPageLabel={translationKey(
              currentFileRequired["SHOPPING_CART"]
            )}
            currentFileRequired={currentFileRequired}
            translationKey={translationKey}
            showHome
          />
          <div className="shopping-cart-wrapper">
            <div
              style={{
                marginTop: "50px",
                width: "70%",
                position: "relative",
              }}
            >
              {displayCartQty > 0 && hasShoppingCartItems ? (
                <>
                  {isBOB && !isCartLocked ? (
                    <div className="lock-unlock-error-msg">
                      {translationKey(
                        currentFileRequired["THE_ORDER_IS_UNLOCK"]
                      )}
                    </div>
                  ) : (
                    ""
                  )}
                  <div className="shopping-cart-card-header">
                    <div className="shopping-cart-card-header-inner">
                      <span className="shopping-cart-header-title">
                        {translationKey(currentFileRequired["SHOPPING_CART"])}
                      </span>
                      <span className="order-id">#{activeOrderId}</span>
                    </div>
                    {isBOB ? (
                      <div className="print-quote-and-cart-locked-wrapper">
                        <ReactToPrint
                          trigger={() => (
                            <div className="print-quote">
                              <img src={PrintIcon} alt="" />
                              <span>
                                {" "}
                                {translationKey(
                                  currentFileRequired["PRINT_QUOTE"]
                                )}
                              </span>
                            </div>
                          )}
                          content={() => printQuoteRef.current}
                        />

                        <div className="cart-locked">
                          <span>
                            {translationKey(currentFileRequired["CART_LOCKED"])}
                          </span>
                          <label className="lock-unlock-switch">
                            <input
                              type="checkbox"
                              checked={isCartLocked}
                              onClick={handleSwitchToggle}
                              disabled={isLockActionLoading}
                            />
                            <span className="slider round"></span>
                          </label>
                        </div>
                      </div>
                    ) : null}
                    {!isBOB && isCartLocked ? (
                      <div className="print-quote-and-cart-locked-wrapper">
                        <div className="cart-locked">
                          <span>
                            {translationKey(currentFileRequired["CART_LOCKED"])}
                          </span>
                          <button
                            type="button"
                            className="button"
                            onClick={UnLockCart}
                            disabled={isLockActionLoading}
                          >
                            {translationKey(currentFileRequired["UNLOCK_CART"])}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  {isBOB ? (
                    <div className="print-quote-hidden-source">
                      <PrintQuote
                        ref={printQuoteRef}
                        orderId={activeOrderId}
                        totalCost={totalCost}
                        shoppingCartItems={activeShoppingCartItems}
                      />
                    </div>
                  ) : null}
                  {isBOB && isCartLocked ? (
                    <div className="apply-discount-wrapper">
                      <span className="select-item-text">
                        {" "}
                        {translationKey(
                          currentFileRequired["SELECT_ITEMS"]
                        )}:{" "}
                      </span>
                      <div className="selection-toggle-wrapper">
                        <button
                          type="button"
                          className="selection-toggle-button"
                          onClick={selectAllItems}
                          disabled={allItemsSelected || !selectableOrderItemIds.length}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="selection-toggle-button"
                          onClick={deselectAllItems}
                          disabled={!hasSelection}
                        >
                          Deselect all
                        </button>
                      </div>
                      <div className="apply-discount-select-wrapper">
                        <select
                          onChange={(e) => {
                            setAppliedDiscountCode(e.target.value);
                          }}
                          id="discount-dropdown"
                          value={appliedDiscountCode}
                        >
                          <option value="">
                            {" "}
                            {translationKey(
                              currentFileRequired["APPLY_A_DISCOUNT"]
                            )}
                          </option>
                          {dicountCodes.length &&
                            dicountCodes.map((dicountCode) => (
                              <option
                                key={dicountCode.promoCode}
                                value={dicountCode.promoCode}
                              >
                                {dicountCode.promoDescription}
                              </option>
                            ))}
                        </select>
                        <button
                          onClick={() => applyDiscountHandler()}
                          className="sec-btn apply-discount-btn"
                          disabled={!isApplyDiscountButtonEnabled}
                          title={
                            !isApplyDiscountButtonEnabled
                              ? `${translationKey(
                                  currentFileRequired["TO_APPLY_A_DISCOUNT"]
                                )}`
                              : ""
                          }
                        >
                          {translationKey(
                            currentFileRequired["APPLY_DISCOUNT"]
                          )}
                        </button>
                        <button
                          onClick={() => removeDiscountHandler()}
                          className="sec-btn remove-discount-btn"
                          disabled={!hasDiscountInSelection}
                          title={
                            !hasDiscountInSelection
                              ? "Select items with discount to remove"
                              : ""
                          }
                        >
                          Remove discount
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => deleteAllItems(e)}
                        className="delete-discount-code"
                        disabled={!hasSelection}
                      >
                        {translationKey(currentFileRequired["DELETE"])}
                      </button>
                    </div>
                  ) : (
                    ""
                  )}
                  {groupedCartItems && groupedCartItems.length
                    ? groupedCartItems.map((group, groupIndex) => {
                        const itemCount =
                          group.itemsQuantity || group.lineItems.length || 0;
                        return (
                          <div
                            className="cart-group"
                            key={`cart-group-${groupIndex}`}
                          >
                            <div className="cart-group-header">
                              <span className="cart-group-title">
                                {group.categoryName || "No Product Category"}
                              </span>
                              <span className="cart-group-count">
                                ({itemCount}{" "}
                                {translationKey(
                                  currentFileRequired["ITEMS"]
                                )}
                                )
                              </span>
                            </div>
                            {group.lineItems.map(
                              (productItem, productIndex) => {
                                return (
                                  <ShoppingCartCardItem
                                    productItem={productItem}
                                    key={`${groupIndex}-${productIndex}`}
                                    setShoppingCartItems={setShoppingCartItems}
                                    shoppingCartItems={activeShoppingCartItems}
                                    storeId={storeId}
                                    getCartItemsCount={getCartItemsCount}
                                    setLoadingCost={setLoadingCost}
                                    orderId={activeOrderId}
                                    baseURL={baseURL}
                                    langId={langId}
                                    catalogId={catalogId}
                                    isBOB={isBOB}
                                    checkHttpStatus={checkHttpStatus}
                                    jsonApiHeader={jsonApiHeader}
                                    setTotalCost={setTotalCost}
                                    refreshCartData={refreshCartData}
                                    isCartLocked={isCartLocked}
                                    translationKey={translationKey}
                                    currentFileRequired={currentFileRequired}
                                    disabled={!isApplyDiscountButtonEnabled}
                                    setIsApplyDiscountButtonEnabled={
                                      setIsApplyDiscountButtonEnabled
                                    }
                                    isSelected={selectedOrderItemIds.includes(
                                      String(productItem?.orderItemId)
                                    )}
                                    onCheckboxChange={onItemSelectionChange}
                                  />
                                );
                              }
                            )}
                          </div>
                        );
                      })
                    : activeShoppingCartItems.map((productItem, productIndex) => {
                        return (
                          <ShoppingCartCardItem
                            productItem={productItem}
                            key={productIndex.toString()}
                            setShoppingCartItems={setShoppingCartItems}
                            shoppingCartItems={activeShoppingCartItems}
                            storeId={storeId}
                            getCartItemsCount={getCartItemsCount}
                            setLoadingCost={setLoadingCost}
                            orderId={activeOrderId}
                            baseURL={baseURL}
                            langId={langId}
                            catalogId={catalogId}
                            isBOB={isBOB}
                            checkHttpStatus={checkHttpStatus}
                            jsonApiHeader={jsonApiHeader}
                            setTotalCost={setTotalCost}
                            refreshCartData={refreshCartData}
                            isCartLocked={isCartLocked}
                            translationKey={translationKey}
                            currentFileRequired={currentFileRequired}
                            disabled={!isApplyDiscountButtonEnabled}
                            setIsApplyDiscountButtonEnabled={
                              setIsApplyDiscountButtonEnabled
                            }
                            isSelected={selectedOrderItemIds.includes(
                              String(productItem?.orderItemId)
                            )}
                            onCheckboxChange={onItemSelectionChange}
                          />
                        );
                      })}
                </>
              ) : null}
              {showEmptyCart ? (
                <ShoppingCartEmptyCard
                  orderFavoritesLink={createLinks.orderFavorites}
                  shopCatalogLink={createLinks.catalog}
                  orderHistoryLink={createLinks.myOrders}
                  translationKey={translationKey}
                  currentFileRequired={currentFileRequired}
                />
              ) : null}
            </div>
            {displayCartQty > 0 && hasShoppingCartItems ? (
              <div className="checkout-container">
                <div className="estimated-cost-section">
                  {translationKey(currentFileRequired["ESTIMATED_SUBTOTAL"])}{" "}
                  <span id="item-count">
                    ({displayCartQty}{" "}
                    {translationKey(currentFileRequired["ITEMS"])})
                  </span>
                </div>

                {loadingCost ? (
                  <ThreeDots
                    height="20"
                    width="20"
                    radius="9"
                    color=""
                    ariaLabel="three-dots-loading"
                    wrapperStyle={{}}
                    wrapperClassName=""
                    visible={true}
                  />
                ) : (
                  <span className="total-cost">
                    {currencyFormatter.format(
                      getDisplayTotalCost(totalCost, shoppingCartItems)
                    )}
                  </span>
                )}
                <p className="tax-message">
                  {translationKey(currentFileRequired["SHIPPING_MESSAGE"])}
                </p>
                <button
                  onClick={() => proceedToCheckoutPage()}
                  className={`proceed-to-checkout-btn ${
                    (isBOB && isCartLocked) || (!isBOB && !isCartLocked)
                      ? ""
                      : "d-none"
                  }`}
                >
                  {translationKey(currentFileRequired["PROCEED_TO_CHECKOUT"])}
                </button>
                <p className="net-price">
                  {translationKey(currentFileRequired["NET_PRICE_DESCRIPTION"])}
                </p>
              </div>
            ) : null}
          </div>
        </>
      )}
    </>
  );
};
export default ShoppingCart;
